/**
 * Display helpers for accounts.enrolment / enrolment_measure.
 * Switch on the measure string only — never tenant, sector, or name.
 */

export function isPopulationServedMeasure(measure) {
  return String(measure ?? '').trim().toLowerCase() === 'population_served';
}

/** Field label. Internal tokens such as population_served are never shown as copy. */
export function enrolmentFieldLabel(measure) {
  return isPopulationServedMeasure(measure) ? 'Population served' : 'Enrolment';
}

/** Shared column header when a list can mix student enrolment and population served. */
export function enrolmentColumnHeader(rows) {
  const list = rows || [];
  if (list.some((row) => isPopulationServedMeasure(row?.enrolment_measure))) return 'Size';
  return 'Enrolment';
}

export function formatEnrolmentNumber(n) {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-US');
}

/**
 * Compact figure for lists. Student rows stay a bare locale number (today's copy).
 * Population served mirrors neo-rag fmtAudience().
 */
export function formatEnrolment(n, measure) {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  if (!isPopulationServedMeasure(measure)) return num.toLocaleString();
  if (num >= 1_000_000) {
    const millions = Number((num / 1_000_000).toFixed(1));
    return `${millions} million residents served`;
  }
  if (num >= 100_000) {
    return `${(Math.round(num / 1000) * 1000).toLocaleString('en-US')} residents served`;
  }
  return `${num.toLocaleString('en-US')} residents served`;
}

/** Header badge: "246,500 residents" vs "246,500 students". */
export function formatEnrolmentBadge(n, measure, studentsNoun = 'students') {
  const formatted = formatEnrolmentNumber(n);
  if (formatted === '—') return formatted;
  if (isPopulationServedMeasure(measure)) return `${formatted} residents`;
  return `${formatted} ${studentsNoun}`;
}

/** Table cell when the column may mix measures: unit lives in the cell. */
export function formatEnrolmentCell(n, measure) {
  if (n == null || n === '') return '';
  const formatted = formatEnrolmentNumber(n);
  if (formatted === '—') return '';
  if (isPopulationServedMeasure(measure)) return `${formatted} residents`;
  return formatted;
}
